import React, { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Github } from "lucide-react";
import { SectionHeader } from "./About";

const CATS = [
  { key: "all", label: "All" },
  { key: "fullstack", label: "Full-stack Website" },
  { key: "ml", label: "Machine Learning" }
];

export default function Projects({ items }) {
  const [cat, setCat] = useState("all");
  if (!items || !items.length) return null;
  const list = cat === "all" ? items : items.filter((p) => p.category === cat);
  const featured = list.filter((p) => p.featured);
  const rest = list.filter((p) => !p.featured);

  return (
    <section id="projects" className="py-24 md:py-32 relative" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <SectionHeader
          number="05"
          overline="Projects"
          title="Shipped agents. Live demos. Real outcomes."
          sub="A selection of fintech, agentic AI, and machine learning projects — most with live Streamlit or GitHub links."
        />

        <div className="mt-14 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition ${
                cat === c.key
                  ? "border-volt text-ink-950 bg-volt"
                  : "border-white/10 text-fog-300 hover:border-volt hover:text-volt"
              }`}
              data-testid={`project-filter-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {featured.length > 0 && (
          <div className="mt-10 grid md:grid-cols-12 gap-6">
            {featured.slice(0, 2).map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`card-tech p-7 relative overflow-hidden ${featured.length === 1 ? "md:col-span-12" : "md:col-span-6"}`}
                data-testid={`project-featured-${i}`}
              >
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none bg-[radial-gradient(circle_at_30%_0%,#CCFF00,transparent_55%)]" />
                <div className="flex items-start justify-between gap-4 relative">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="overline text-volt">FEATURED / {p.category.toUpperCase()}</span>
                      {p.live && (
                        <span className="inline-flex items-center gap-2 text-[11px] text-neon-green tracking-wider">
                          <span className="dot-live"></span> LIVE
                        </span>
                      )}
                    </div>
                    <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-fog-200 leading-relaxed">{p.description}</p>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-1.5 relative">
                  {p.tech.map((t) => (
                    <span key={t} className="tag-chip">{t}</span>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-4 relative">
                  {p.demo_url && (
                    <a href={p.demo_url} target="_blank" rel="noreferrer" className="btn-volt" data-testid={`project-demo-${i}`}>
                      <ExternalLink className="w-4 h-4" /> Live Demo
                    </a>
                  )}
                  {p.repo_url && (
                    <a href={p.repo_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`project-repo-${i}`}>
                      <Github className="w-4 h-4" /> Code
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="card-tech p-5 flex flex-col"
              data-testid={`project-card-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="overline">{p.category.toUpperCase()}</span>
                {p.live && <span className="dot-live"></span>}
              </div>
              <h4 className="mt-2 font-display font-semibold text-lg">{p.title}</h4>
              <p className="mt-2 text-fog-300 text-sm line-clamp-3">{p.description}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.tech.slice(0, 5).map((t) => (
                  <span key={t} className="tag-chip">{t}</span>
                ))}
              </div>
              <div className="mt-auto pt-5 flex gap-3">
                {p.demo_url && (
                  <a href={p.demo_url} target="_blank" rel="noreferrer" className="text-volt hover:text-white text-sm inline-flex items-center gap-1">
                    Demo <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {p.repo_url && (
                  <a href={p.repo_url} target="_blank" rel="noreferrer" className="text-fog-300 hover:text-volt text-sm inline-flex items-center gap-1">
                    Code <Github className="w-3 h-3" />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
